import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { setGitPathHttpHeader } from '@y42/feature/git';
import { BackendError, getBackendErrorMessage, ORG_API_URL_PARAM } from '@y42/model';
import { areArraysEqual } from '@y42/util/are-arrays-equal';
import { formatSizeUnits } from '@y42/util/format-size-units';
import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  AllDatasourceTablesQuery,
  DatasourceColumnUIModel,
  DatasourceTableState,
  DatasourceTableUIModel,
  TableColumnsQuery,
  TableColumnsService,
} from '../../state';
import { CardinalityModel } from '../cardinality/cardinality.model';
import { TableCardinalityService } from '../cardinality/table-cardinality.service';
import { AggridDatasourceBuilder } from './services/aggrid-datasource.builder';

interface TableViewerSSRMResponse {
  rows: unknown[];
}

type TableViewerGridApiError = BackendError<{ msg: string }>;

const DEFAULT_MESSAGE = 'No Data to Display.';

@UntilDestroy()
@Component({
  selector: 'y42-table-viewer[tableId]',
  templateUrl: './table-viewer.component.html',
  styleUrls: ['./table-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableCardinalityService, AggridDatasourceBuilder],
})
export class TableViewerComponent {
  constructor(
    private readonly aggridDatasourceBuilder: AggridDatasourceBuilder,
    private readonly tableColumnsService: TableColumnsService,
    private readonly tableColumnsQuery: TableColumnsQuery,
    private readonly allDatasourcesQuery: AllDatasourceTablesQuery,
    private readonly tableCardinalityService: TableCardinalityService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    this.initGetDataListener();
  }

  @Input()
  @InputObservable()
  tableId?: getIDType<DatasourceTableState>;
  private readonly tableId$!: Observable<getIDType<DatasourceTableState> | undefined>;

  private serverSideDataSourceTablePath = '';
  private readonly errorMessage$ = new BehaviorSubject<string>(DEFAULT_MESSAGE);

  private readonly cardinality$ = new BehaviorSubject<CardinalityModel | undefined>(undefined);
  private readonly cardinalityLoading$ = new BehaviorSubject<boolean>(true);
  private readonly activeTable$ = new BehaviorSubject<DatasourceTableUIModel | undefined>(undefined);
  private readonly noView$ = new BehaviorSubject<boolean>(false);
  private readonly size$ = new BehaviorSubject<string>('');
  private readonly rows$ = new BehaviorSubject<number>(0);
  private readonly previewRows$ = new BehaviorSubject<number>(0);

  // display detailed chart for each column
  private readonly activeHeaderColumn$ = new BehaviorSubject<DatasourceColumnUIModel | undefined>(undefined);
  private readonly activeCardinality$ = new BehaviorSubject<CardinalityModel | undefined>(undefined);
  private readonly activeCardinalityLoading$ = new BehaviorSubject<boolean>(false);
  private readonly serverSideDatasource$ = new BehaviorSubject<IServerSideDatasource | undefined>(undefined);

  public readonly state = connectState(this, {
    columns: this.tableColumnsQuery.selectEnhancedUiColumns$,
    cardinality: this.cardinality$,
    cardinalityLoading: this.cardinalityLoading$,
    activeTable: this.activeTable$,
    noView: this.noView$,
    size: this.size$,
    rows: this.rows$,
    previewRows: this.previewRows$,
    activeHeaderColumn: this.activeHeaderColumn$,
    activeCardinality: this.activeCardinality$,
    activeCardinalityLoading: this.activeCardinalityLoading$,
    serverSideDatasource: this.serverSideDatasource$,
    errorMessage: this.errorMessage$,
  });

  analyzeColumn(field_name: string | null) {
    this.resetActiveColumn();

    const foundColumn = this.tableColumnsQuery.getAllEnhancedUiColumns().find((col) => col.field_name === field_name);
    if (!!field_name && !!foundColumn) {
      this.activeHeaderColumn$.next(foundColumn);
      this.activeCardinalityLoading$.next(true);

      if (this.activeTableId) {
        this.tableCardinalityService
          .getById(this.serverSideDataSourceTablePath, field_name, { previewLimit: 1000 })
          .pipe(
            tap((res) => {
              this.activeCardinalityLoading$.next(false);
              this.activeCardinality$.next(res);
            }),
            catchError(() => {
              this.resetActiveColumn();
              return of(null);
            }),
            untilDestroyed(this),
          )
          .subscribe();
      }
    }
  }

  private initGetDataListener() {
    this.tableId$
      .pipe(
        tap(() => this.resetSettings()),
        debounceTime(500),
        switchMap((tableId) => {
          const table$ = tableId ? this.allDatasourcesQuery.selectUIEntity(tableId) : of(undefined);
          return combineLatest([
            table$.pipe(
              distinctUntilChanged((tablePrev, tableNext) => isEqual(tablePrev, tableNext)),
              debounceTime(500),
            ),
            this.allDatasourcesQuery.tables$.pipe(
              distinctUntilChanged((a, b) => areArraysEqual(a, b)),
              debounceTime(500),
            ), // selectUIEntity doesnt consider childQuery / jobs
          ]).pipe(
            map(([table, __]) => table),
            distinctUntilChanged((tablePrev, tableNext) => isEqual(tablePrev, tableNext)),
            debounceTime(100),
            tap((foundTable) => this.setActiveTable(foundTable)),
            tap((foundTable) => this.setColumns(foundTable)),
            switchMap((foundTable) => {
              return this.setCardinality(foundTable);
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private setActiveTable(foundTable: DatasourceTableUIModel | undefined) {
    this.activeTable$.next(foundTable);

    if (foundTable?.latest_valid_job) {
      this.rows$.next(foundTable.latest_valid_job?.details?.row_count || 0);
      this.size$.next(formatSizeUnits(foundTable.latest_valid_job?.details?.size_in_bytes || 0));
    } else {
      this.rows$.next(0);
      this.size$.next('0');
    }
  }

  private setColumns(foundTable: DatasourceTableUIModel | undefined) {
    if (foundTable) {
      const tablePath = `/${foundTable?.$path}`;
      const columns = foundTable.latest_valid_job?.columns || [];
      if (!columns.length) {
        this.errorHandlerService.handleError(
          new Error('[DATASOURCE][TABLE-VIEWER] No columns available to render job table.'),
        );
      }

      this.tableColumnsService.setColumns(columns);
      this.setServerSideDatasource(tablePath);
      this.resetActiveColumn();
    } else {
      this.tableColumnsService.setColumns([]);
    }
  }

  private setCardinality(foundTable: DatasourceTableUIModel | undefined) {
    if (foundTable) {
      return this.tableCardinalityService.getAll(`/${foundTable?.$path}`).pipe(
        tap((res) => {
          this.noView$.next(false);
          this.errorMessage$.next(DEFAULT_MESSAGE);
          this.cardinality$.next(res);
          this.cardinalityLoading$.next(false);
        }),
        catchError((error: unknown) => {
          if (
            (error as HttpErrorResponse).error &&
            (error as HttpErrorResponse).error.message === 'table has no view yet'
          ) {
            this.noView$.next(true);
          }
          this.cardinality$.next(undefined);
          this.cardinalityLoading$.next(false);
          return of(null);
        }),
        untilDestroyed(this),
      );
    } else {
      return of(null);
    }
  }

  private setServerSideDatasource(tablePath: string) {
    this.serverSideDataSourceTablePath = tablePath;
    const serverSideUrl = `${environment.url.analytic_api}/company/${ORG_API_URL_PARAM}/grid`;
    const columns = this.tableColumnsQuery.getAllEnhancedUiColumns();

    this.serverSideDatasource$.next(
      this.aggridDatasourceBuilder.build<TableViewerSSRMResponse>(
        serverSideUrl,
        columns,
        this.responseCallback.bind(this),
        {
          requestBuilder: this.requestBuilder.bind(this),
          responseErrorCallback: this.responseErrorCallback.bind(this),
          headers: setGitPathHttpHeader(tablePath),
        },
      ),
    );
  }

  private responseErrorCallback(error: unknown, getRowParams: IServerSideGetRowsParams) {
    const err = getBackendErrorMessage<TableViewerGridApiError>(error, 'Error fetching data');
    this.errorMessage$.next(err);
    this.noView$.next(true);

    getRowParams.fail();
  }

  private responseCallback(res: TableViewerSSRMResponse, getRowParams: IServerSideGetRowsParams) {
    let lastRow = -1;
    this.previewRows$.next(res.rows?.length + (getRowParams.request.startRow || 0));
    const diff = (getRowParams.request.endRow || 0) - (getRowParams.request.startRow || 0);
    if (res.rows && res.rows.length < diff) {
      lastRow = res.rows.length + (getRowParams.request.startRow || 0);
    }

    getRowParams.success({
      rowData: res.rows,
      rowCount: lastRow,
    });
  }

  private requestBuilder(requestParams: IServerSideGetRowsParams) {
    return { ...requestParams.request, table: this.serverSideDataSourceTablePath };
  }

  private resetSettings() {
    this.activeTable$.next(undefined);
    this.rows$.next(0);
    this.size$.next('0');
    this.tableColumnsService.setColumns([]);
    this.serverSideDatasource$.next(undefined);
    this.cardinality$.next(undefined);
    this.cardinalityLoading$.next(true);
  }

  private resetActiveColumn() {
    this.activeHeaderColumn$.next(undefined);
    this.activeCardinality$.next(undefined);
    this.activeCardinalityLoading$.next(false);
  }

  private get activeTableId() {
    return this.activeTable$.value?.$path;
  }
}
